// src/hooks/useUniversalTranslation.js
import { useCallback, useEffect, useMemo, useState } from 'react';

const DEFAULT_API_BASE = 'https://ourzestapp.online/oz_backend/api';
const configuredBase = typeof window !== 'undefined' && window.OURZEST_API_BASE
    ? window.OURZEST_API_BASE
    : (typeof process !== 'undefined' && process.env?.REACT_APP_OURZEST_API_BASE)
        || DEFAULT_API_BASE;
const API_BASE = configuredBase.replace(/\/$/, '');
const CACHE_STORAGE_KEY = 'ourzest_translation_cache_v3';
const CACHE_TTL = 30 * 24 * 60 * 60 * 1000;
const MAX_STORED_TRANSLATIONS = 3000;
const LOCAL_DATA_TRANSLATIONS = {
    ar: {
        'adil khan': 'عادل خان', 'adil super store': 'متجر عادل سوبر',
        mhb: 'إم إتش بي', 'oz test': 'اختبار أو زد', 'hafiz sab': 'حافظ صاحب',
        bk: 'بي كي', ghous: 'غوث', khanooo: 'خانو', gull: 'غُل',
        'walk-in customer': 'عميل بدون موعد'
    },
    ur: {
        'adil khan': 'عادل خان', 'adil super store': 'عادل سپر اسٹور',
        mhb: 'ایم ایچ بی', 'oz test': 'او زیڈ ٹیسٹ', 'hafiz sab': 'حافظ صاحب',
        bk: 'بی کے', ghous: 'غوث', khanooo: 'خانو', gull: 'گل',
        'walk-in customer': 'واک اِن گاہک'
    }
};

const memoryCache = new Map();
const pendingBatches = new Map();
let storageLoaded = false;

const fallbackLanguages = [
    { language_code: 'en', language_name: 'English', native_name: 'English', flag_emoji: '🇬🇧', is_rtl: 0 },
    { language_code: 'ur', language_name: 'Urdu', native_name: 'اردو', flag_emoji: '🇵🇰', is_rtl: 1 },
    { language_code: 'ps', language_name: 'Pashto', native_name: 'پښتو', flag_emoji: '🇦🇫', is_rtl: 1 },
    { language_code: 'ar', language_name: 'Arabic', native_name: 'العربية', flag_emoji: '🇸🇦', is_rtl: 1 },
    { language_code: 'fa', language_name: 'Farsi', native_name: 'فارسی', flag_emoji: '🇮🇷', is_rtl: 1 },
    { language_code: 'prs', language_name: 'Dari', native_name: 'دری', flag_emoji: '🇦🇫', is_rtl: 1 },
    { language_code: 'tr', language_name: 'Turkish', native_name: 'Türkçe', flag_emoji: '🇹🇷', is_rtl: 0 }
];

const cacheKey = (text, source, target) => `${source}\u0000${target}\u0000${text}`;

function loadStorageCache() {
    if (storageLoaded || typeof localStorage === 'undefined') return;
    storageLoaded = true;
    try {
        const stored = JSON.parse(localStorage.getItem(CACHE_STORAGE_KEY) || '{}');
        const now = Date.now();
        Object.entries(stored).forEach(([key, entry]) => {
            if (entry && entry.expiresAt > now && typeof entry.value === 'string') {
                memoryCache.set(key, entry);
            }
        });
    } catch (_) {
        localStorage.removeItem(CACHE_STORAGE_KEY);
    }
}

function saveStorageCache() {
    if (typeof localStorage === 'undefined') return;
    try {
        const entries = [...memoryCache.entries()]
            .sort((left, right) => right[1].savedAt - left[1].savedAt)
            .slice(0, MAX_STORED_TRANSLATIONS);
        localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch (_) {
        // Storage may be disabled or full; the in-memory cache still works.
    }
}

async function readJsonResponse(response) {
    const raw = await response.text();
    let payload = null;
    try {
        payload = raw ? JSON.parse(raw.replace(/^\uFEFF/, '')) : null;
    } catch (_) {
        throw new Error(`Server returned invalid JSON (${response.status})`);
    }
    if (!response.ok || payload?.status !== 'success') {
        throw new Error(payload?.message || `Server error ${response.status}`);
    }
    return payload;
}

async function requestTranslationChunk(texts, target, source, signal) {
    const signature = JSON.stringify([source, target, texts]);
    if (pendingBatches.has(signature)) return pendingBatches.get(signature);

    const request = fetch(`${API_BASE}/translate_batch.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ texts, source, target }),
        signal
    })
        .then(readJsonResponse)
        .then((payload) => payload.translations)
        .finally(() => pendingBatches.delete(signature));
    pendingBatches.set(signature, request);
    return request;
}

async function translateInBrowser(texts, targetLanguage, sourceLanguage, signal) {
    const results = [...texts];
    const source = sourceLanguage === 'prs' ? 'fa' : sourceLanguage;
    const target = targetLanguage === 'prs' ? 'fa' : targetLanguage;
    let cursor = 0;

    const worker = async () => {
        while (cursor < texts.length) {
            const index = cursor++;
            const text = texts[index];
            try {
                const url = 'https://translate.googleapis.com/translate_a/single'
                    + `?client=gtx&sl=${encodeURIComponent(source)}`
                    + `&tl=${encodeURIComponent(target)}&dt=t&q=${encodeURIComponent(text)}`;
                const response = await fetch(url, { signal });
                if (!response.ok) continue;
                const payload = await response.json();
                const translated = Array.isArray(payload?.[0])
                    ? payload[0].map((segment) => segment?.[0] || '').join('')
                    : '';
                if (translated.trim()) results[index] = translated;
            } catch (error) {
                if (error.name === 'AbortError') throw error;
            }
        }
    };

    await Promise.all(
        Array.from({ length: Math.min(8, texts.length) }, () => worker())
    );
    return results;
}

export async function translateTextBatch(texts, targetLanguage, sourceLanguage = 'en', options = {}) {
    const input = Array.isArray(texts) ? texts.map((text) => String(text ?? '')) : [];
    if (!input.length || !targetLanguage || targetLanguage === sourceLanguage) return input;

    loadStorageCache();
    const now = Date.now();
    const result = [...input];
    const missing = new Map();

    input.forEach((text, index) => {
        if (!text.trim()) return;
        const localValue = LOCAL_DATA_TRANSLATIONS[targetLanguage]?.[text.trim().toLowerCase()];
        if (localValue) {
            result[index] = localValue;
            return;
        }
        const key = cacheKey(text, sourceLanguage, targetLanguage);
        const cached = memoryCache.get(key);
        if (cached?.expiresAt > now) {
            result[index] = cached.value;
        } else {
            if (!missing.has(text)) missing.set(text, []);
            missing.get(text).push(index);
        }
    });
    if (!missing.size) return result;

    const uniqueTexts = [...missing.keys()];
    const chunks = [];
    for (let index = 0; index < uniqueTexts.length; index += 150) {
        chunks.push(uniqueTexts.slice(index, index + 150));
    }

    let translatedValues;
    try {
        const translatedChunks = await Promise.all(
            chunks.map((chunk) => requestTranslationChunk(
                chunk,
                targetLanguage,
                sourceLanguage,
                options.signal
            ))
        );
        translatedValues = translatedChunks.flat();
    } catch (error) {
        if (error.name === 'AbortError') throw error;
        translatedValues = [...uniqueTexts];
    }

    // Some shared hosts cannot make outbound cURL requests. Retry unchanged
    // values from the browser, where the existing app already has internet access.
    const unchangedPositions = [];
    const unchangedTexts = [];
    uniqueTexts.forEach((sourceText, index) => {
        if (!translatedValues[index] || translatedValues[index] === sourceText) {
            unchangedPositions.push(index);
            unchangedTexts.push(sourceText);
        }
    });
    if (unchangedTexts.length) {
        const browserTranslations = await translateInBrowser(
            unchangedTexts,
            targetLanguage,
            sourceLanguage,
            options.signal
        );
        unchangedPositions.forEach((position, index) => {
            translatedValues[position] = browserTranslations[index];
        });
    }

    translatedValues.forEach((translated, index) => {
        const sourceText = uniqueTexts[index];
        const value = typeof translated === 'string' && translated.trim() ? translated : sourceText;
        missing.get(sourceText).forEach((originalIndex) => { result[originalIndex] = value; });
        // The PHP service returns the original text when every provider is down.
        // Do not persist that fallback, so the next language switch can retry.
        if (value !== sourceText) {
            memoryCache.set(cacheKey(sourceText, sourceLanguage, targetLanguage), {
                value,
                savedAt: now,
                expiresAt: now + CACHE_TTL
            });
        }
    });
    saveStorageCache();
    return result;
}

export function useUniversalTranslation(texts, targetLanguage, sourceLanguage = 'en') {
    const normalisedTexts = useMemo(
        () => (Array.isArray(texts) ? texts.map((text) => String(text ?? '')) : []),
        // A value signature prevents effects from looping when callers create a new array each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(texts || [])]
    );
    const [translatedTexts, setTranslatedTexts] = useState(normalisedTexts);
    const [isTranslating, setIsTranslating] = useState(false);
    const [error, setError] = useState(null);

    const translateBatch = useCallback(
        (items, target = targetLanguage, options = {}) =>
            translateTextBatch(items, target, sourceLanguage, options),
        [sourceLanguage, targetLanguage]
    );

    useEffect(() => {
        const controller = new AbortController();
        setError(null);
        if (!normalisedTexts.length || targetLanguage === sourceLanguage) {
            setTranslatedTexts(normalisedTexts);
            setIsTranslating(false);
            return () => controller.abort();
        }

        setIsTranslating(true);
        translateTextBatch(normalisedTexts, targetLanguage, sourceLanguage, { signal: controller.signal })
            .then(setTranslatedTexts)
            .catch((requestError) => {
                if (requestError.name !== 'AbortError') {
                    setError(requestError);
                    setTranslatedTexts(normalisedTexts);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsTranslating(false);
            });
        return () => controller.abort();
    }, [normalisedTexts, sourceLanguage, targetLanguage]);

    return { translatedTexts, isTranslating, error, translateBatch };
}

export function useAvailableLanguages() {
    // Keep language selection synchronous and available offline. The backend
    // endpoint remains optional for other clients, but this hook never depends on it.
    return { languages: fallbackLanguages, loading: false, error: null };
}
